import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  IndianRupee,
  Clock3,
} from "lucide-react";

import BaseTable from "../../table/BaseTable";
import PageContainer from "../../common/PageContainer";

const data = [
  {
    id: 1,
    name: "John Doe",
    email: "john.doe@example.com",
    industry: "Real Estate",
    plan: "Standard",
    usedMinutes: 742,
    includedMinutes: 1000,
    estimatedBill: 25000,
    status: "No Overage",
    lastActive: "2 hours ago",
  },
  {
    id: 2,
    name: "Sarah Lee",
    email: "sarah.lee@example.com",
    industry: "Healthcare",
    plan: "Premium",
    usedMinutes: 1240,
    includedMinutes: 2000,
    estimatedBill: 38500,
    status: "No Overage",
    lastActive: "15 mins ago",
  },
  {
    id: 3,
    name: "Mike Ross",
    email: "mike.ross@example.com",
    industry: "Finance",
    plan: "Standard",
    usedMinutes: 980,
    includedMinutes: 1000,
    estimatedBill: 31200,
    status: "Near Limit",
    lastActive: "Yesterday",
  },
  {
    id: 4,
    name: "Emma Watson",
    email: "emma.w@example.com",
    industry: "Retail",
    plan: "Basic",
    usedMinutes: 1350,
    includedMinutes: 1000,
    estimatedBill: 42800,
    status: "Overage",
    lastActive: "5 mins ago",
  },
];

function StatusBadge({ status }) {
  const map = {
    "No Overage": {
      bg: "#ecfdf3",
      color: "#027a48",
    },
    "Near Limit": {
      bg: "#fff7ed",
      color: "#ea580c",
    },
    Overage: {
      bg: "#fef2f2",
      color: "#dc2626",
    },
  };

  const style = map[status];

  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      ● {status}
    </span>
  );
}

function PlanBadge({ plan }) {
  const config = {
    Premium: {
      bg: "#eef4ff",
      color: "#4338ca",
    },
    Standard: {
      bg: "#f5f5f5",
      color: "#374151",
    },
    Basic: {
      bg: "#f9fafb",
      color: "#6b7280",
    },
  };

  const style = config[plan];

  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {plan}
    </span>
  );
}

export default function AgentUsageList() {
  const navigate = useNavigate();

  const columns = [
    {
      title: "Agent",
      dataIndex: "name",
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "#eef2ff",
              color: "#4338ca",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {row.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>

          <div>
            <div
              style={{
                fontWeight: 600,
                cursor: "pointer",
                color: "#111827",
              }}
              onClick={() =>
                navigate(`/agents/${row.id}`)
              }
            >
              {row.name}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              {row.email}
            </div>
          </div>
        </div>
      ),
    },

    {
      title: "Industry",
      dataIndex: "industry",
      render: (value) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Building2 size={16} />
          {value}
        </div>
      ),
    },

    {
      title: "Plan",
      dataIndex: "plan",
      render: (value) => (
        <PlanBadge plan={value} />
      ),
    },

    {
      title: "Usage",
      dataIndex: "usedMinutes",
      render: (_, row) => {
        const percentage = Math.round(
          (row.usedMinutes /
            row.includedMinutes) *
            100
        );

        const progressColor =
          percentage >= 100
            ? "#dc2626"
            : percentage >= 90
            ? "#ea580c"
            : "#2563eb";

        return (
          <div
            style={{
              minWidth: 180,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
                fontSize: 13,
              }}
            >
              <span>
                {row.usedMinutes} mins
              </span>

              <strong>
                {percentage}%
              </strong>
            </div>

            <div
              style={{
                height: 8,
                background: "#e5e7eb",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(
                    percentage,
                    100
                  )}%`,
                  height: "100%",
                  background:
                    progressColor,
                }}
              />
            </div>
          </div>
        );
      },
    },

    {
      title: "Monthly Bill",
      dataIndex: "estimatedBill",
      render: (value) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 600,
          }}
        >
          <IndianRupee size={15} />
          {value.toLocaleString("en-IN")}
        </div>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <StatusBadge status={status} />
      ),
    },

    {
      title: "Last Active",
      dataIndex: "lastActive",
      render: (value) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "#6b7280",
          }}
        >
          <Clock3 size={14} />
          {value}
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <div
        style={{
          marginBottom: 20,
        }}
      >
        <h1
          className="text-primary"
          style={{
            marginBottom: 4,
          }}
        >
          Agent Usage
        </h1>

        <p
          style={{
            color: "#6b7280",
            fontSize: 14,
          }}
        >
          Monitor usage, billing, and
          activity across all voice agents.
        </p>
      </div>

      <BaseTable
        columns={columns}
        data={data}
      />
    </PageContainer>
  );
}